/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   unmeasuredSize.tsx
 *     Feature         :   286 - FCM licence enforcement and estate limits (T159 items 4 and 32)
 *     Description     :   How a total says it counts sizes the last collection did not measure. The merge keeps a
 *                         lakehouse's or warehouse's earlier storage_size_gb when the worker cannot size it and records
 *                         size_measurement_error (Spec 238), so a total over those items counts their LAST KNOWN size.
 *                         Item 4 set the rule on the lakehouse list's Total Size tile: keep the size in the total and
 *                         say how many are counted that way. Every total that sums the column uses these.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { AppTooltip } from '@/components/ui';

/** The subtext of a total over items the last collection did not size; undefined when there are none. */
export function unmeasuredSizeNote(count: number | null | undefined): string | undefined {
  const n = Number(count) || 0;
  return n > 0 ? `${n} not measured on the last collection, counted at their last known size` : undefined;
}

/** For a size inside a table row (a workspace's or a capacity's total): "(N not measured)", explained in a tooltip. */
export const UnmeasuredSizeTag: React.FC<{ count: number | null | undefined }> = ({ count }) => {
  const note = unmeasuredSizeNote(count);
  if (!note) return null;
  return (
    <AppTooltip title={note}>
      <span className="ml-1 text-xs text-[var(--color-text-secondary)]" data-testid="unmeasured-size-tag">
        ({Number(count)} not measured)
      </span>
    </AppTooltip>
  );
};
